import { environment } from "src/environments/environment";
import { EventEmitter, Injectable } from "@angular/core";
import { UserSession } from "../models/usersession";

@Injectable({
  providedIn: "root",
})
export class UserSessionService {
  session = new UserSession();
  private tokenKey = "eGramam-Token";
  private sessionKey = "eGramam-Session";

  private decodedToken: any = null; // cached token payload
  filterArr: any[];

  blockChangeEvent = new EventEmitter<void>();
  SubDivisionChange = new EventEmitter<void>();
  refresh = new EventEmitter<void>();
  financialYearChangeEvent = new EventEmitter<number>();
  private currentFinancialYearId: number = 0;
  private actualCurrentFinancialYearId: number = 0;

  emitFinancialYearChange(financialYearId: number) {
    this.currentFinancialYearId = financialYearId;
    this.financialYearChangeEvent.emit(financialYearId);
  }

  getFinancialYearId() {
    return this.currentFinancialYearId;
  }

  setActualCurrentFinancialYearId(financialYearId: number) {
    this.actualCurrentFinancialYearId = financialYearId;
  }

  isCurrentFinancialYear(financialYearId: number) {
    return this.actualCurrentFinancialYearId === financialYearId;
  }

  constructor() {
    this.cleanupOldLocalStorage();
  }

  /** Clean up old localStorage */
  private cleanupOldLocalStorage() {
    const oldKey = "eGramam-" + environment.apiBaseUrl + "-AuthData";
    localStorage.removeItem(oldKey);
    localStorage.removeItem("role");
  }

  /** Get JWT from localStorage */
  authToken(): string {
    return localStorage.getItem(this.tokenKey) || "";
  }

  /** Decode token and cache it */
  private getDecodedToken(): any {
    if (this.decodedToken) return this.decodedToken;

    const token = this.authToken();
    if (!token) return null;

    try {
      const payload = JSON.parse(atob(token.split(".")[1]));
      this.decodedToken = payload;
      return payload;
    } catch (e) {
      console.error("Invalid token", e);
      return null;
    }
  }

  getFromToken<T = any>(key: string, defaultValue: T = null): T {
    const payload = this.getDecodedToken();
    return payload?.[key] ?? defaultValue;
  }

  /** Create session */
  create(session: any) {
    if (session?.authToken) {
      localStorage.setItem(this.tokenKey, session.authToken);
    }
  }

  setdecodedToken(session: any) {
    this.decodedToken = session;
  }

  /** Destroy session */
  destroy() {
    localStorage.removeItem(this.tokenKey);
    localStorage.removeItem(this.sessionKey);
    localStorage.removeItem("role");
    localStorage.removeItem("menucontrols");
    this.cleanupOldLocalStorage();
    this.decodedToken = null;
    localStorage.clear();
  }

  load(): UserSession {
    const jsondata = this.decodedToken;
    return jsondata;
  }

  /** Emitters */
  emitBlockChange() {
    this.blockChangeEvent.emit();
  }
  emitSubDivisionChange() {
    this.SubDivisionChange.emit();
  }
  emitRefresh() {
    this.refresh.emit();
  }

  userId() {
    return Number(this.getFromToken<number>("user.id", 0));
  }
  getUserName() {
    return this.getFromToken<string>("user.fullname", "");
  }
  roleId() {
    return Number(this.getFromToken("user.roleid", 0));
  }
  roleName(): string {
    return this.getFromToken<string>("user.rolename", "");
  }
  getEmail(): string {
    return this.getFromToken<string>("user.email", "");
  }
  isDynamicPassword(): string {
    return String(this.getFromToken<boolean>("user.isDynamicPassword", false));
  }
  getLanguageType() {
    return Number(this.getFromToken<number>("user.languagetype", 0));
  }

  getFilterregionId() {
    return this.getFromToken<any>("filterregionId", 0);
  }
  getFilterDivisionId() {
    return this.getFromToken<any>("filterdivisionId", 0);
  }
  getFilterSubDivisionId() {
    return this.getFromToken<any>("filtersubdivisionId", 0);
  }
  getFilterBlockId() {
    return this.getFromToken<any>("filterblockId", 0);
  }

  getClientId() {
    return Number(this.getFromToken("user.clientId", 0));
  }
  getCurrentClientId() {
    return Number(this.getFromToken("user.clientId", 0));
  }
  getCurrentClientName(): string {
    return this.getFromToken<string>("user.clientName", "");
  }

  getRegionId() {
    return this.getFromToken<any>("user.regionId", 0);
  }
  getdistrictId() {
    return this.getFromToken<any>("user.districtId", 0);
  }
  getDivisionId() {
    return this.getFromToken<any>("user.divisionId", 0);
  }
  getSubDivisionId() {
    return this.getFromToken<any>("user.subDivisionId", 0);
  }
  getBlockId() {
    return this.getFromToken<any>("user.blockId", 0);
  }
  userDistrictName() {
    return this.getFromToken<any>("user.userDistrictName", "");
  }
  userBlockName() {
    return this.getFromToken<any>("user.userBlockName", "");
  }

  getUserdistrictId() {
    return this.getFromToken<any>("user.userDistrictId", 0);
  }
  getUserBlockId() {
    return this.getFromToken<any>("user.userBlockId", 0);
  }
  getUserVillageId() {
    return this.getFromToken<any>("user.userVillageId", 0);
  }
  getUserGramPanjayatId() {
    return this.getFromToken<any>("user.userGramPanjayatId", 0);
  }
  getUserdepartmentId() {
    return this.getFromToken<any>("user.departmentId", 0);
  }

  resultData(): any[] {
    return this.getFromToken<any[]>("resultdata", []);
  }

  referrence2() {
    const dept = this.getFromToken<string>("user.departmentId", null);
    return dept ? JSON.parse(dept).Services : null;
  }

  getLocalStorageWithKey(key: any) {
    return localStorage.getItem(key);
  }
  setLocalStorageWithKey(key: any, session: any) {
    localStorage.setItem(key, JSON.stringify(session));
  }

  getPageUrl(key: any) {
    this.filterArr = [];
    const menuData = this.getLocalStorageWithKey("menucontrols");
    if (!menuData) return null;

    const menu = JSON.parse(menuData);
    menu.forEach((e) => {
      if (e.submenu) e.submenu.forEach((el) => this.filterArr.push(el));
    });

    const output = this.filterArr.find((e) => e.path === key);
    return output?.controlAccess;
  }
}
